import { Link } from "@/lib/router-compat";
import { ArrowRight, Phone } from "lucide-react";
import { serviceCategoriesData, getServicesByCategory } from "@/data/site/services";
import { SiteSEOHead } from "@/components/site/SiteSEOHead";
import { siteConfig } from "@/config/siteConfig";
import { categoryIcons } from "@/data/site/navigation";

const SiteServices = () => {
  return (
    <main className="min-h-screen bg-background pt-20">
      <SiteSEOHead
        title="Tax Attorney Services | Tax Lawyer for IRS Help | McCauley Law"
        description="Full-service tax attorney and tax lawyer representing individuals and businesses before the IRS. Criminal tax defense, audit representation, Offer in Compromise, penalty abatement & more."
        canonical={`${siteConfig.url}/services`}
      />

      {/* Hero */}
      <section className="relative -mt-[72px] pt-[calc(72px+3rem)] pb-16 md:pb-20 bg-gradient-navy">
        <div className="container mx-auto px-6 text-center relative z-10 max-w-3xl">
          <p className="text-gold font-semibold uppercase tracking-wider mb-3 text-xs sm:text-sm">Every IRS Weapon. Every Legal Counter.</p>
          <h1 className="text-[2rem] leading-[1.1] sm:text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-5 text-balance">
            The IRS Has A Playbook. <span className="text-gold">We Wrote The Counter‑Playbook.</span>
          </h1>
          <p className="text-primary-foreground/80 text-base sm:text-lg text-pretty leading-relaxed border-l-2 border-gold/60 pl-4 text-left sm:text-center sm:border-0 sm:pl-0">
            Audits. Liens. Levies. Garnishments. Criminal investigations. Whatever the government is doing to you right now, we've already beaten it — thousands of times. Below is exactly how.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-16 max-w-5xl mx-auto">
            {serviceCategoriesData.map((cat) => (
              <Link
                key={cat.slug}
                to={`/services/${cat.slug}`}
                className="group bg-card border border-border border-l-4 border-l-gold/60 p-6 md:p-8 hover:border-gold/50 hover:border-l-gold hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  {(() => {
                    const Icon = categoryIcons[`/services/${cat.slug}`];
                    return Icon ? <Icon className="w-10 h-10" aria-hidden="true" /> : null;
                  })()}
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground group-hover:text-gold transition-colors text-balance">
                    {cat.title}
                  </h2>
                </div>
                <p className="text-muted-foreground mb-4 text-pretty leading-relaxed">{cat.tagline}</p>
                <span className="inline-flex items-center gap-2 text-gold font-semibold text-sm uppercase tracking-wider">
                  See How We Win <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>

          {/* All services listed */}
          {serviceCategoriesData
            .filter((c) => c.slug === "criminal-tax-issues" || c.slug === "tax-resolution-services")
            .map((cat) => {
            const services = getServicesByCategory(cat.slug);
            return (
              <div key={cat.slug} className="mb-14 md:mb-16">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-6 pb-3 border-b-2 border-gold/20 text-balance">
                  {cat.title}
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((svc) => (
                    <Link
                      key={svc.slug}
                      to={`/services/${cat.slug}/${svc.slug}`}
                      className="group bg-card border border-border border-l-2 border-l-gold/40 p-5 hover:border-gold/40 hover:border-l-gold hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                    >
                      <h3 className="font-semibold text-foreground group-hover:text-gold transition-colors mb-2 text-balance">
                        {svc.title}
                      </h3>
                      <p className="text-sm text-muted-foreground text-pretty leading-relaxed">{svc.shortDescription}</p>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-navy py-14 md:py-16">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <p className="text-gold font-semibold uppercase tracking-wider mb-3 text-xs sm:text-sm">Not Sure Where You Stand?</p>
          <h2 className="text-[1.75rem] leading-[1.15] sm:text-3xl font-heading font-bold text-primary-foreground mb-4 text-balance">
            One Honest Call. <span className="text-gold">Zero Pressure.</span>
          </h2>
          <p className="text-primary-foreground/75 mb-8 text-base sm:text-lg text-pretty leading-relaxed">
            Tell a real tax attorney what's happening — for free, protected by attorney‑client privilege from the second you say hello. You'll know exactly what the IRS can do and exactly how we'll stop them.
          </p>
          <a
            href={`tel:${siteConfig.phoneClean}`}
            className="inline-flex items-center gap-3 bg-cta text-cta-foreground font-bold px-8 py-4 hover:bg-cta-hover transition-colors shadow-cta animate-pulse-cta text-base sm:text-lg"
          >
            <Phone className="w-5 h-5" />
            Call {siteConfig.phone} Now
          </a>
          <p className="mt-5 text-xs text-primary-foreground/50">100% free · 100% confidential · No call center — actual attorneys</p>
        </div>
      </section>

    </main>
  );
};

export default SiteServices;
